import React, { useState, useEffect } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Home as HomeIcon, PlayCircle, BarChart3, Settings, CreditCard, Sparkles, User, LogOut, MessageCircle, Swords, FlaskConical } from 'lucide-react';
import Home from './components/Home';
import ResultsHome from './components/ResultsHome';
import ComparePage from './components/ComparePage';
import BenchmarkRunner from './components/BenchmarkRunner';
import Dashboard from './components/Dashboard';
import { withAdmin2FA } from './components/Admin2FA';
const AdminDashboardBase = React.lazy(() => import('./components/AdminDashboard'));
const AdminDashboard = withAdmin2FA(AdminDashboardBase);
import AuthModal from './components/AuthModal';
import UserProfileDropdown from './components/UserProfileDropdown';
import Pricing from './components/Pricing';
import UserProfile from './components/UserProfile';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AccessibilityStatement from './components/AccessibilityStatement';
import CookieConsent from './components/CookieConsent';
import ResultDetail from './components/ResultDetail';
import ProtectedRoute from './components/ProtectedRoute';
import { ToastContainer } from './components/Toast';
import ResetPassword from './components/ResetPassword';
import { checkCreditsAndWarn } from './components/Toast';
import VerifyEmail from './components/VerifyEmail';
import InviteGate from './components/InviteGate';
import Blog from './components/Blog';
import { ResultsProvider } from './context/ResultsContext';
import FeedbackWidget, { FeedbackIcon } from './components/FeedbackWidget';
import FeedPage from './components/FeedPage';
import GroupChat from './components/GroupChat';
import ChatDetail from './components/ChatDetail';
import ToneLab from './components/ToneLab';
import ToneLabDetail from './components/ToneLabDetail';

import {
  initGA,
  trackPageView,
  trackLogin,
  trackLogout,
  trackEvent,
  setUserId,
  setUserProperties
} from './utils/GoogleAnalytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

function AppContent() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [user, setUser] = useState(undefined);
  const [showFeedbackTrigger, setShowFeedbackTrigger] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize GA on mount
  useEffect(() => {
    initGA();
  }, []);

  // Track page views on route change
  useEffect(() => {
    const pageTitles = {
      '/': 'Home',
      '/compare': 'Compare AI',
      '/benchmark': 'Run Benchmark',
      '/dashboard': 'Analytics',
      '/pricing': 'Pricing',
      '/profile': 'User Profile',
      '/admin': 'Admin Dashboard',
      '/privacy': 'Privacy Policy',
      '/terms': 'Terms of Service',
      '/accessibility': 'Accessibility Statement',
      '/group-chat': 'AI Group Chat',
      '/tonelab': 'AI Tone'
    };
    const title = pageTitles[location.pathname] || 'MidoLABS';
    trackPageView(location.pathname, title);
  }, [location]);

  // ✅ Scroll to top on route change
  useEffect(() => {
    if (location.pathname === '/') {
      window.scrollTo(0, 0);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.pathname]);

  // Reset feedback trigger when leaving benchmark page
  useEffect(() => {
    if (location.pathname !== '/benchmark') {
      setShowFeedbackTrigger(false);
    }
  }, [location.pathname]);

  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      return;
    }

    try {
        const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const userData = {
          ...data,
          is_admin: data.is_admin || data.role === 'admin'
        };
        setUser(userData);
        checkCreditsAndWarn(data);

        // Set user properties for analytics
        setUserId(data.email);
        setUserProperties({
          plan_tier: data.plan_tier || 'free',
          is_premium: data.is_premium || false
        });
      } else {
        localStorage.removeItem('token');
        setUser(null);
      }
    } catch (error) {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  useEffect(() => {
    if (user) {
      checkCreditsAndWarn(user);
    }
  }, [user]);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsAuthModalOpen(false);
    trackLogin('email');
    trackEvent('Auth', 'Login Success');
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsMenuOpen(false);
    trackLogout();
  };

  // Refresh user data (for credits update)
  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const handleOpenAuthModal = () => {
    setIsAuthModalOpen(true);
    trackEvent('Auth', 'Open Modal');
  };

  // Callback for when benchmark completes successfully
  const handleBenchmarkComplete = () => {
    setShowFeedbackTrigger(true);
  };

  // ✅ Desktop navigation with scroll
  const handleNavClick = (path) => {
    if (path === '/') {
      window.scrollTo(0, 0);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // ✅ Mobile navigation with scroll
  const handleMobileNavigate = (path) => {
    setIsMenuOpen(false);
    navigate(path);
    // Small delay to let the menu close first
    setTimeout(() => {
      if (path === '/') {
        window.scrollTo(0, 0);
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" role="status" aria-label="Loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" aria-hidden="true"></div>
          <p className="text-gray-600">Loading MidoLABS.ai ...</p>
        </div>
      </div>
    );
  }
      {/* background-color */}

  return (

    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50/30 flex flex-col">
      <ToastContainer />

      {/* ===== PORTFOLIO BANNER ===== */}
      <div className="w-full bg-amber-400 text-gray-900 text-center py-2 px-4 text-sm font-medium z-[60] relative">
        👋 Portfolio demo project by <strong>Yakov Miller</strong> — browse freely, running is disabled.{' '}
        <a
          href="https://www.linkedin.com/in/yakovmiller-86n5/"
          target="_blank"
          rel="noopener noreferrer"
          className="underline font-bold hover:text-gray-700"
        >
          Find me on LinkedIn
        </a>
      </div>
      {/* ===== END PORTFOLIO BANNER ===== */}

      {/* Skip to main content - Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-lg focus:outline-none"
      >
        Skip to main content
      </a>

      {/* ✅ FLOATING NAVIGATION - Same width as content container */}
      <nav
        className="sticky top-0 z-50 pt-2 sm:pt-4 px-4 sm:px-0"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-6xl mx-auto px-0 sm:px-6">
            <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-gray-200/80 shadow-sm sm:shadow-md">
            <div className="px-4 sm:px-6">
              <div className="flex items-center justify-between h-14">

              {/* Logo */}
              <Link
                to="/"
                onClick={() => handleNavClick('/')}
                className="flex items-center flex-shrink-0"
                aria-label="MidoLABS.ai Home"
              >
                <img
                  src="/logo-header.png"
                  alt="MidoLABS"
                  className="h-7 sm:h-8"
                />
              </Link>




              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-1" role="menubar">
                <Link
                  to="/"
                  onClick={() => handleNavClick('/')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${location.pathname === '/' ? 'bg-primary-100 text-primary-600' : 'text-gray-700 hover:bg-gray-100'}`}
                  role="menuitem"
                  aria-current={location.pathname === '/' ? 'page' : undefined}
                >
                  <HomeIcon size={18} aria-hidden="true" />
                  <span className="font-medium">Home</span>
                </Link>

                {/* AI Chat - before Compare */}
                <Link
                  to="/group-chat"
                  onClick={() => handleNavClick('/group-chat')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${location.pathname === '/group-chat' || location.pathname.startsWith('/chat/') ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'text-gray-700 hover:bg-gray-100'}`}
                  role="menuitem"
                  aria-current={location.pathname === '/group-chat' || location.pathname.startsWith('/chat/') ? 'page' : undefined}
                >
                  <MessageCircle size={18} aria-hidden="true" />
                  <span className="font-medium">AI Chat</span>
                </Link>

                <Link
                  to="/compare"
                  onClick={() => handleNavClick('/compare')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${location.pathname === '/compare' || location.pathname === '/benchmark' || location.pathname.startsWith('/result/') ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'text-gray-700 hover:bg-gray-100'}`}
                  role="menuitem"
                  aria-current={location.pathname === '/compare' || location.pathname.startsWith('/result/') ? 'page' : undefined}
                >
                  <Swords size={18} aria-hidden="true" />
                  <span className="font-medium">AI Compare</span>
                </Link>

                {/* AI Tone */}
                <Link
                  to="/tonelab"
                  onClick={() => handleNavClick('/tonelab')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${location.pathname === '/tonelab' || location.pathname.startsWith('/tonelab/') ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'text-gray-700 hover:bg-gray-100'}`}
                  role="menuitem"
                  aria-current={location.pathname === '/tonelab' || location.pathname.startsWith('/tonelab/') ? 'page' : undefined}
                >
                  <FlaskConical size={18} aria-hidden="true" />
                  <span className="font-medium">AI Tone</span>
                </Link>

                {/* Community Feed - HIDDEN FOR NOW
                <Link
                  to="/feed"
                  onClick={() => handleNavClick('/feed')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${location.pathname === '/feed' ? 'bg-primary-100 text-primary-600' : 'text-gray-700 hover:bg-gray-100'}`}
                  role="menuitem"
                  aria-current={location.pathname === '/feed' ? 'page' : undefined}
                >
                  <MessageCircle size={18} aria-hidden="true" />
                  <span className="font-medium">Community</span>
                </Link>
                */}

                <Link
                  to="/dashboard"
                  onClick={() => handleNavClick('/dashboard')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${location.pathname === '/dashboard' ? 'bg-primary-100 text-primary-600' : 'text-gray-700 hover:bg-gray-100'}`}
                  role="menuitem"
                  aria-current={location.pathname === '/dashboard' ? 'page' : undefined}
                >
                  <BarChart3 size={18} aria-hidden="true" />
                  <span className="font-medium">Analytics</span>
                </Link>

                {/* PRICING HIDDEN - portfolio demo
                <Link
                  to="/pricing"
                  onClick={() => handleNavClick('/pricing')}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${location.pathname === '/pricing' ? 'bg-primary-100 text-primary-600' : 'text-gray-700 hover:bg-gray-100'}`}
                  role="menuitem"
                  aria-current={location.pathname === '/pricing' ? 'page' : undefined}
                >
                  <CreditCard size={18} aria-hidden="true" />
                  <span className="font-medium">Pricing</span>
                </Link>
                */}

                {user?.is_admin && (
                  <Link
                    to="/admin"
                    onClick={() => handleNavClick('/admin')}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-all ${location.pathname === '/admin' ? 'bg-orange-100 text-orange-600' : 'text-orange-600 hover:bg-orange-50'}`}
                    role="menuitem"
                    aria-current={location.pathname === '/admin' ? 'page' : undefined}
                  >
                    <Settings size={18} aria-hidden="true" />
                    <span className="font-medium">Admin</span>
                  </Link>
                )}
              </div>

              {/* Right side - User/Auth */}
              <div className="hidden lg:flex items-center space-x-3">
                {user ? (
                  <>
                    <div className="flex items-center space-x-2 px-3 py-1.5 bg-primary-50 rounded-lg border border-primary-200" aria-label={`You have ${user.credits || 0} credits`}>
                      <Sparkles size={16} className="text-primary-600" aria-hidden="true" />
                      <span className="text-sm font-semibold text-primary-600">{user.credits || 0}</span>
                    </div>
                    <UserProfileDropdown user={user} onLogout={handleLogout} />
                  </>
                ) : (
                  /* SIGN IN HIDDEN - portfolio demo
                  <button
                    onClick={() => setIsAuthModalOpen(true)}
                    className="px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-lg font-medium hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    aria-label="Sign in to your account"
                  >
                    Sign In
                  </button>
                  */
                  null
                )}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-expanded={isMenuOpen}
                aria-controls="mobile-menu"
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu Button visibility handled above */}
          </div>
        </div>
      </nav>

      {/* ✅ FLOATING MOBILE MENU - Glassmorphism Style */}
      {isMenuOpen && (
        <>
          {/* Dark Overlay */}
          <div
            className="lg:hidden fixed inset-0 bg-black/30 z-40 transition-opacity duration-200"
            onClick={() => setIsMenuOpen(false)}
            aria-hidden="true"
            style={{ animation: 'fadeIn 0.2s ease-out' }}
          />

          {/* Floating Menu Card - FIXED: Same width as nav */}
            <div
              id="mobile-menu"
              className="lg:hidden fixed top-16 left-0 right-0 z-50 px-4 transition-all duration-200"
              role="menu"
              style={{ animation: 'slideDown 0.25s ease-out' }}
            >
            <div className="max-w-6xl mx-auto px-0 sm:px-4">
                <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/80 shadow-xl overflow-hidden">
                  {/* Navigation Links */}
                  <div className="p-3 space-y-1">
                    <button
                      onClick={() => handleMobileNavigate('/')}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${location.pathname === '/' ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-50'}`}
                      role="menuitem"
                    >
                      <HomeIcon size={20} className={location.pathname === '/' ? 'text-primary-600' : 'text-gray-600'} aria-hidden="true" />
                      <span className="font-medium">Home</span>
                    </button>

                    {/* AI Chat - before Compare */}
                    <button
                      onClick={() => handleMobileNavigate('/group-chat')}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${location.pathname === '/group-chat' || location.pathname.startsWith('/chat/') ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
                      role="menuitem"
                    >
                      <MessageCircle size={20} className={location.pathname === '/group-chat' || location.pathname.startsWith('/chat/') ? 'text-blue-600' : 'text-gray-600'} aria-hidden="true" />
                      <span className="font-medium">AI Chat</span>
                    </button>

                    <button
                      onClick={() => handleMobileNavigate('/compare')}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${location.pathname === '/compare' || location.pathname === '/benchmark' || location.pathname.startsWith('/result/') ? 'bg-violet-50 text-violet-700' : 'hover:bg-gray-50'}`}
                      role="menuitem"
                    >
                      <Swords size={20} className={location.pathname === '/compare' || location.pathname === '/benchmark' || location.pathname.startsWith('/result/') ? 'text-violet-600' : 'text-gray-600'} aria-hidden="true" />
                      <span className="font-medium">AI Compare</span>
                    </button>

                    {/* AI Tone */}
                    <button
                      onClick={() => handleMobileNavigate('/tonelab')}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${location.pathname === '/tonelab' || location.pathname.startsWith('/tonelab/') ? 'bg-rose-50 text-rose-700' : 'hover:bg-gray-50'}`}
                      role="menuitem"
                    >
                      <FlaskConical size={20} className={location.pathname === '/tonelab' || location.pathname.startsWith('/tonelab/') ? 'text-rose-600' : 'text-gray-600'} aria-hidden="true" />
                      <span className="font-medium">AI Tone</span>
                    </button>

                    {/* Community - HIDDEN FOR NOW
                    <button
                      onClick={() => handleMobileNavigate('/feed')}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${location.pathname === '/feed' ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-50'}`}
                      role="menuitem"
                    >
                      <MessageCircle size={20} className={location.pathname === '/feed' ? 'text-primary-600' : 'text-gray-600'} aria-hidden="true" />
                      <span className="font-medium">Community</span>
                    </button>
                    */}

                    <button
                      onClick={() => handleMobileNavigate('/dashboard')}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${location.pathname === '/dashboard' ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-50'}`}
                      role="menuitem"
                    >
                      <BarChart3 size={20} className={location.pathname === '/dashboard' ? 'text-primary-600' : 'text-gray-600'} aria-hidden="true" />
                      <span className="font-medium">Analytics</span>
                    </button>

                    {/* PRICING HIDDEN - portfolio demo
                    <button
                      onClick={() => handleMobileNavigate('/pricing')}
                      className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${location.pathname === '/pricing' ? 'bg-primary-100 text-primary-600' : 'hover:bg-gray-50'}`}
                      role="menuitem"
                    >
                      <CreditCard size={20} className={location.pathname === '/pricing' ? 'text-primary-600' : 'text-gray-600'} aria-hidden="true" />
                      <span className="font-medium">Pricing</span>
                    </button>
                    */}

                    {user?.is_admin && (
                      <button
                        onClick={() => handleMobileNavigate('/admin')}
                        className={`flex items-center space-x-3 w-full px-4 py-3 rounded-xl transition-all ${location.pathname === '/admin' ? 'bg-orange-100 text-orange-600' : 'text-orange-600 hover:bg-orange-50'}`}
                        role="menuitem"
                      >
                        <Settings size={20} aria-hidden="true" />
                        <span className="font-medium">Admin</span>
                      </button>
                    )}
                  </div>

                  {/* User Section */}
                  <div className="bg-gray-100/80 p-3 border-t border-gray-200/50">
                    {user ? (
                      <div className="space-y-2">
                        {/* User Info Card */}
                        <div className="flex items-center justify-between px-4 py-3 bg-white/70 rounded-xl">
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <div className="w-8 h-8 bg-gradient-to-br from-primary-400 to-secondary-400 rounded-full flex items-center justify-center flex-shrink-0">
                              <User size={16} className="text-white" aria-hidden="true" />
                            </div>
                            <span className="text-sm font-medium text-gray-800 truncate">{user.email}</span>
                          </div>
                          <div className="flex items-center space-x-1 px-2 py-1 bg-primary-100 rounded-lg text-primary-600 flex-shrink-0" aria-label={`${user.credits || 0} credits`}>
                            <Sparkles size={14} aria-hidden="true" />
                            <span className="text-sm font-bold">{user.credits || 0}</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleMobileNavigate('/profile')}
                            className="flex-1 flex items-center justify-center space-x-2 px-4 py-2.5 bg-white/70 rounded-xl hover:bg-white transition-all"
                            role="menuitem"
                          >
                            <User size={18} className="text-gray-600" aria-hidden="true" />
                            <span className="font-medium text-gray-700">Profile</span>
                          </button>
                          <button
                            onClick={handleLogout}
                            className="flex items-center justify-center space-x-2 px-4 py-2.5 bg-red-50 rounded-xl hover:bg-red-100 transition-all text-red-600"
                            role="menuitem"
                          >
                            <LogOut size={18} aria-hidden="true" />
                            <span className="font-medium">Logout</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* SIGN IN HIDDEN - portfolio demo
                      <button
                        onClick={() => { setIsAuthModalOpen(true); setIsMenuOpen(false); }}
                        className="w-full px-4 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white rounded-xl font-medium hover:shadow-lg transition-all"
                      >
                        Sign In
                      </button>
                      */
                      null
                    )}
                  </div>

                  {/* Legal Links */}
                  <div className="px-4 py-2 bg-gray-50/50 flex items-center justify-center space-x-3 text-xs text-gray-400">
                    <button onClick={() => handleMobileNavigate('/privacy')} className="hover:text-gray-600 transition-colors">Privacy</button>
                    <span aria-hidden="true">•</span>
                    <button onClick={() => handleMobileNavigate('/terms')} className="hover:text-gray-600 transition-colors">Terms</button>
                    <span aria-hidden="true">•</span>
                    <button onClick={() => handleMobileNavigate('/accessibility')} className="hover:text-gray-600 transition-colors">Accessibility</button>
                  </div>
                </div>
              </div>
            </div>

          {/* CSS Animations */}
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideDown {
              from {
                opacity: 0;
                transform: translateY(-10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
          `}</style>
        </>
      )}

      {/* ✅ MAIN CONTENT - Pages handle their own container/width */}
      <main id="main-content" className="flex-1 pt-2 pb-6" role="main">
        <Routes>
          <Route path="/" element={<Home user={user} />} />
          <Route
            path="/compare"
            element={
              <ComparePage
                user={user}
                onOpenLogin={() => setIsAuthModalOpen(true)}
                onCreditsUpdate={refreshUser}
                onBenchmarkComplete={handleBenchmarkComplete}
              />
            }
          />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/accessibility" element={<AccessibilityStatement />} />
          <Route path="/result/:runId" element={<ResultDetail />} />
          <Route path="/chat/:chatId" element={<ChatDetail user={user} />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          {/* PRICING HIDDEN - portfolio demo
          <Route path="/pricing" element={<Pricing user={user} onOpenAuth={handleOpenAuthModal} />} />
          */}
          <Route path="/feed" element={<FeedPage user={user} onOpenLogin={() => setIsAuthModalOpen(true)} />} />
          <Route path="/group-chat" element={<GroupChat user={user} onCreditsUpdate={refreshUser} />} />
          <Route path="/tonelab" element={<ToneLab user={user} onCreditsUpdate={refreshUser} />} />
          <Route path="/tonelab/:id" element={<ToneLabDetail user={user} />} />
          <Route
            path="/benchmark"
            element={
              <BenchmarkRunner
                user={user}
                onCreditsUpdate={refreshUser}
                onBenchmarkComplete={handleBenchmarkComplete}
              />
            }
          />
          <Route path="/dashboard" element={<Dashboard user={user} />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/profile" element={<ProtectedRoute user={user}><UserProfile user={user} onLogout={handleLogout} /></ProtectedRoute>} />
          <Route
              path="/admin"
              element={
                <React.Suspense fallback={
                  <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                  </div>
                }>
                  <ProtectedRoute user={user} requireAdmin={true}>
                    <AdminDashboard user={user} />
                  </ProtectedRoute>
                </React.Suspense>
              }
            />
        </Routes>
      </main>

    {/* FOOTER - FLOATING STYLE (matches header) */}
    <footer className="pb-2 sm:pb-4 px-4 sm:px-0 mt-auto" role="contentinfo">
      <div className="max-w-6xl mx-auto px-0 sm:px-6">
        <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-gray-200/80 shadow-sm sm:shadow-md">
          <div className="py-3 px-4 sm:px-6">
          {/* Mobile: Stack vertically, Desktop: Row */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">

            {/* Logo + Copyright */}
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Link to="/" className="flex items-center hover:opacity-80" aria-label="MidoLABS.ai Home">
                <img src="/logo-footer.png" alt="MidoLABS.ai" className="h-6 sm:h-7"/>
              </Link>
              <span aria-hidden="true">•</span>
              <span>© 2025</span>
            </div>

            {/* Links - wrap on small screens */}
            <nav className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-gray-500" aria-label="Footer navigation">
              <Link to="/blog" className="hover:text-blue-600">Blog</Link>
              <Link to="/privacy" className="hover:text-primary-600 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-primary-600 transition-colors">Terms</Link>
              <Link to="/accessibility" className="hover:text-primary-600 transition-colors">Accessibility</Link>
              <button
                onClick={() => { localStorage.removeItem('cookieConsent'); window.location.reload(); }}
                className="hover:text-primary-600 transition-colors cursor-pointer"
                aria-label="Manage cookie preferences"
              >
                Cookies
              </button>
            </nav>

            {/* Social Icons + Feedback */}
            <div className="flex items-center space-x-3">
              {/* Feedback Icon - Only for logged-in users */}
              {user && (
                <FeedbackIcon onClick={() => setShowFeedbackModal(true)} />
              )}

            </div>
          </div>
          </div>
        </div>
        </div>
      </footer>

      {/* MODAL */}
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} onLogin={handleLoginSuccess} />
      <CookieConsent />

      {/* FEEDBACK WIDGET */}
      <FeedbackWidget
        user={user}
        triggerAfterBenchmark={showFeedbackTrigger}
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />

    </div>
  );
}

function App() {
  return (
    <ResultsProvider>
      <Router>
          <AppContent />
      </Router>
      <Analytics />
    </ResultsProvider>
  );
}

export default App;